import React, { RefObject, useEffect, useMemo, useRef, useState } from "react";
import classNames from "classnames";
import RoundActionButton from "@/components/ui/buttons/round-action-button";
import AppBtn from "@/components/ui/buttons/app-btn";
import { CartItem, ProductItemInterface } from "@/assets/interfaces";
import useClickOutside from "@/components/hooks/useClickOutside";
import QuantityUpdate from "../items/quantity-update";

interface AddSingleItemToCartProps {
  isDropUpOpen: Boolean;
  setIsDropUpOpen: (isDropUpOpen: boolean) => void;
  formatAsCurrency: (amount: number) => string;
  openItem: (item: ProductItemInterface) => void;
  item: ProductItemInterface;
  cart: CartItem[];
  saveToCart: (item: ProductItemInterface, quantity: number) => void;
}

const AddSingleItemToCart: React.FC<AddSingleItemToCartProps> = ({
  isDropUpOpen,
  formatAsCurrency,
  openItem,
  setIsDropUpOpen,
  item,
  cart,
  saveToCart,
}) => {
  const dropUpRef = useRef<HTMLDivElement>(null);
  const [quantity, setQuantity] = useState(1);

  const itemIsInCart = useMemo(() => {
    return cart.some((cartItem) => cartItem.item_id === item.id);
  }, [cart, item]);

  useClickOutside(dropUpRef, () => {
    setIsDropUpOpen(false);
  });

  useEffect(() => {
    const cartItem = cart.find((cartItem) => cartItem.item_id === item.id);
    if (cartItem) {
      setQuantity(cartItem.quantity);
    }
  }, [cart, item]);

  const selectItem = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    if (item) {
      openItem(item);
    }
  };

  const discountExists = useMemo(() => {
    return typeof item?.discount_price === "number" && item.discount_price > 0 && item.available_to_purchase;
  }, [item]);

  return (
    <div
      ref={dropUpRef}
      onClick={(e) => e.stopPropagation()}
      className={`absolute left-4.5 right-4.5 bottom-[20px] z-[49] bg-white border border-[#E5E5E580] mx-auto transform ease origin-bottom-left transition-all duration-300 rounded-[12.5px] shadow-card flex flex-col ${
        isDropUpOpen ? "h-auto p-3 scale-100 opacity-100  gap-y-1" : "pointer-events-none h-0 scale-50 opacity-0"
      }`}
      // style={{ transitionTimingFunction: "cubic-bezier(1,-0.05,.66,.28)" }}
    >
      <div className="flex items-start justify-between mb-2.5 border-b border-grey-divider pb-2.5">
        <div
          className={classNames("text-1xs sm:text-sm text-grey-subtext flex flex-col", {
            "text-opacity-100": item?.available_to_purchase,
            "text-opacity-70 font-medium": !item?.available_to_purchase,
            "flex items-center": !discountExists,
            "opacity-0": !isDropUpOpen,
          })}
        >
          {discountExists && item?.available_to_purchase && (
            <div className="flex items-center flex-wrap">
              <div
                className={`text-black font-display font-bold text-base mr-1.5 tracking-tighter ${
                  item?.available_to_purchase ? "text-opacity-100" : "text-opacity-70"
                }`}
                style={{ letterSpacing: "0 !important" }}
              >
                {formatAsCurrency((item?.discount_price ?? 0) * (quantity || 1))}
              </div>
            </div>
          )}
          <div
            className={classNames(
              {
                "font-display font-bold line-through text-sm text-placeholder":
                  discountExists && item?.available_to_purchase,
                "font-display font-bold text-base text-black": !discountExists,
                "font-bold": item?.available_to_purchase,
                "!text-placeholder": !item?.available_to_purchase,
              },
              "tracking-tighter"
            )}
          >
            {formatAsCurrency((item?.price ?? 0) * (discountExists && item?.available_to_purchase ? 1 : quantity || 1))}{" "}
            {/* show actual price if discount exists, otherwise show quantity * price */}
          </div>
        </div>
        <RoundActionButton onClick={selectItem} className=" w-7 h-7 ml-2 hover:bg-grey-fields-200">
          {/* prettier-ignore */}
          <svg className="w-4.5" viewBox="0 0 15 15" fill="none">
            <path d="M7.5 13.75C10.9375 13.75 13.75 10.9375 13.75 7.5C13.75 4.0625 10.9375 1.25 7.5 1.25C4.0625 1.25 1.25 4.0625 1.25 7.5C1.25 10.9375 4.0625 13.75 7.5 13.75Z" stroke="#747478" strokeLinecap="round" strokeLinejoin="round" />
            <path d="M7.5 5V8.125" stroke="#747478" strokeLinecap="round" strokeLinejoin="round" />
            <path d="M7.49609 10H7.50171" stroke="#747478" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </RoundActionButton>
      </div>
      <div className="flex w-full justify-between items-center bg-white pb-2 px-0">
        <dt className="text-1xs sm:text-sm  text-black"> Quantity</dt>
        <dd className={`no-outline inline-flex items-center rounded-full flex-shrink-0`}>
          <QuantityUpdate
            value={quantity}
            onChange={(qty: number) => setQuantity(qty)}
            increment={() => setQuantity(quantity + 1)}
            decrement={() => setQuantity(quantity - 1)}
            deleteAction={() => setQuantity(0)}
            showDeleteAction={false}
          />
        </dd>
      </div>

      <AppBtn
        isBlock
        onClick={() => (item?.available_to_purchase ? saveToCart(item, quantity) : null)}
        className={`mt-2.5 add-to-cart-vid-toggle-${item?.id}`}
        size="lg"
      >
        {item?.available_to_purchase ? (itemIsInCart ? "Update Cart" : "Add To Cart") : "Item Unavailable"}
      </AppBtn>
    </div>
  );
};

export default AddSingleItemToCart;
